/**
 * URL- und Playback-Helfer für Bunny ohne Upload-Abhängigkeiten.
 * Diese Datei darf kein tus-js-client importieren.
 */
export const HEADER_VIDEO_BUNNY_ID = "438f7935-d407-4812-8b7b-a437b86621f0";

/**
 * Playback-URL-Optionen für Bunny-Videos
 */
export interface BunnyPlaybackUrls {
  /** Primäre URL (CDN Host, falls gesetzt) */
  primary: string;
  /** Fallback-URL (MediaDelivery, immer verfügbar wenn Library ID gesetzt) */
  fallback: string | null;
  /** Welche URL sollte verwendet werden? */
  recommended: string;
}

/**
 * Erstellt Playback-URLs für ein Bunny-Video (HLS)
 * Gibt sowohl CDN-URL als auch MediaDelivery-URL zurück, falls beide verfügbar sind
 */
export function getBunnyPlaybackUrls(bunnyVideoId: string): BunnyPlaybackUrls {
  const cdnHost = import.meta.env.VITE_BUNNY_CDN_HOST;
  const libraryId = import.meta.env.VITE_BUNNY_LIBRARY_ID;

  const cdnUrl = cdnHost
    ? `https://${cdnHost}/${bunnyVideoId}/playlist.m3u8`
    : null;

  const mediaDeliveryUrl = libraryId
    ? `https://v${libraryId}.mediadelivery.net/${bunnyVideoId}/playlist.m3u8`
    : null;

  if (cdnUrl && mediaDeliveryUrl) {
    return {
      primary: cdnUrl,
      fallback: mediaDeliveryUrl,
      recommended: cdnUrl,
    };
  }

  if (cdnUrl) {
    console.warn(
      "⚠️ VITE_BUNNY_CDN_HOST ist gesetzt, aber VITE_BUNNY_LIBRARY_ID fehlt. " +
        "Falls die Pull Zone deaktiviert ist, wird die URL nicht funktionieren. " +
        "Setze VITE_BUNNY_LIBRARY_ID für automatischen Fallback."
    );
    return {
      primary: cdnUrl,
      fallback: null,
      recommended: cdnUrl,
    };
  }

  if (mediaDeliveryUrl) {
    return {
      primary: mediaDeliveryUrl,
      fallback: null,
      recommended: mediaDeliveryUrl,
    };
  }

  throw new Error(
    "VITE_BUNNY_CDN_HOST oder VITE_BUNNY_LIBRARY_ID muss in .env gesetzt sein."
  );
}

/**
 * Erstellt eine Playback-URL für ein Bunny-Video (HLS)
 */
export function getBunnyPlaybackUrl(bunnyVideoId: string): string {
  const urls = getBunnyPlaybackUrls(bunnyVideoId);
  return urls.recommended;
}

/**
 * Video-Qualitäts-Level für netzwerkbasierte Auswahl
 */
export type VideoQuality = "480p" | "720p" | "1080p" | "adaptive";

/**
 * Erstellt eine Playback-URL für ein Bunny-Video.
 * Nutzt HLS adaptiv; _networkType bleibt für Rückwärtskompatibilität erhalten.
 */
export function getBunnyPlaybackUrlForNetwork(
  bunnyVideoId: string,
  _networkType?: "wifi" | "cellular" | "unknown"
): string {
  const cdnHost = import.meta.env.VITE_BUNNY_CDN_HOST;
  const libraryId = import.meta.env.VITE_BUNNY_LIBRARY_ID;

  const baseUrl = cdnHost
    ? `https://${cdnHost}/${bunnyVideoId}`
    : `https://v${libraryId}.mediadelivery.net/${bunnyVideoId}`;

  return `${baseUrl}/playlist.m3u8`;
}

/**
 * Helper-Funktion: Normalisiert Pull Zone URL
 */
function bunnyBase(pullZone: string): string {
  return pullZone.includes(".")
    ? `https://${pullZone}`
    : `https://${pullZone}.b-cdn.net`;
}

/**
 * Erstellt HLS Playback-URL für Bunny Video
 */
export function bunnyHlsUrl(pullZone: string, videoId: string): string {
  return `${bunnyBase(pullZone)}/${videoId}/playlist.m3u8`;
}

/**
 * Erstellt Thumbnail-URL für Bunny Video
 */
export function bunnyThumbUrl(
  pullZone: string,
  videoId: string,
  file = "thumbnail.jpg"
): string {
  return `${bunnyBase(pullZone)}/${videoId}/${file}`;
}

/**
 * Erstellt Preview Animation URL für Bunny Video
 */
export function bunnyPreviewUrl(pullZone: string, videoId: string): string {
  return `${bunnyBase(pullZone)}/${videoId}/preview.webp`;
}

/**
 * Erstellt MP4 Fallback-URL für Bunny Video
 */
export function bunnyMp4Url(
  pullZone: string,
  videoId: string,
  height = 720
): string {
  return `${bunnyBase(pullZone)}/${videoId}/play_${height}p.mp4`;
}

/**
 * Erstellt Bunny URLs basierend auf Environment-Variablen
 */
export function getBunnyUrls(videoId: string): {
  hls: string;
  thumbnail: string;
  preview: string;
  mp4: string;
  pullZone: string;
} {
  const cdnHost = import.meta.env.VITE_BUNNY_CDN_HOST;
  const libraryId = import.meta.env.VITE_BUNNY_LIBRARY_ID;

  const pullZone =
    cdnHost || (libraryId ? `v${libraryId}.mediadelivery.net` : "");

  if (!pullZone) {
    throw new Error(
      "VITE_BUNNY_CDN_HOST oder VITE_BUNNY_LIBRARY_ID muss in .env gesetzt sein."
    );
  }

  return {
    hls: bunnyHlsUrl(pullZone, videoId),
    thumbnail: bunnyThumbUrl(pullZone, videoId),
    preview: bunnyPreviewUrl(pullZone, videoId),
    mp4: bunnyMp4Url(pullZone, videoId),
    pullZone,
  };
}
